// src/components/Review/ReplyInputBox.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, auth } from "../../Security/firebase-config";
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  increment,
  getDoc,
} from "firebase/firestore";
import { useTeam } from "../../context/TeamContext";
import { notifyTeamMembersExceptActor } from "../../utils/TeamNotifier";
import CustomAlert from "../core-components/CustomAlert";

import MentionMenu from "./MentionMenu";
import {addPlayerMentions} from './playerMentions';
import "./ReplyInputBox.css";

// same normalizeRole you used in NoteInputBox
const normalizeRole = (raw) => {
  const r = (raw || "player").toString().trim().toLowerCase();
  if (r === "subs" || r === "substitute" || r === "substitutes") return "sub";
  if (r === "manger" || r === "managers") return "manager";
  if (r === "spectators") return "spectator";
  if (r === "players") return "player";
  if (r === "trialing" || r === "trials") return "trial";
  return r;
};

export default function ReplyInputBox({
  mapId,
  matchId,
  videoId,
  noteId,
  onReplyAdded,
  onCancel,
  localSubmit,
  teamIdOverride,
  usernameOverride,
  membersOverride,
}) {
  const context = useTeam();
  const teamId = teamIdOverride ?? context.teamId, username = usernameOverride ?? context.username;
  const [localSaving, setLocalSaving] = useState(false);

  const [replyText, setReplyText] = useState("");
  const [alert, setAlert] = useState(null);

  // ─────────────────────────────────────────────
  // Mention system (same pattern as NoteInputBox)
  // ─────────────────────────────────────────────
  const textareaRef = useRef(null);

  const [members, setMembers] = useState([]);
  useEffect(() => {
    if (membersOverride) {setMembers(membersOverride.map(m=>({...m,roleLower:normalizeRole(m.roleLower||m.role)})));return;}
    if (!teamId) return;

    (async () => {
      const teamRef = doc(db, "teams", teamId);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) return;

      const data = teamSnap.data() || {};
      const memberIds = data.members || [];
      const teamRoles = data.teamRoles || {};

      const rows = await Promise.all(
        memberIds.map(async (uid) => {
          const uRef = doc(db, "uids", uid);
          const uSnap = await getDoc(uRef);
          if (!uSnap.exists()) return null;

          const uname = uSnap.data()?.username;
          if (!uname) return null;

          return {
            uid,
            username: uname,
            roleLower: normalizeRole(teamRoles[uid] || "player"),
          };
        })
      );

      setMembers(rows.filter(Boolean));
    })();
  }, [teamId, membersOverride]);

  const byRole = useMemo(() => {
    const base = {
      player: [],
      sub: [],
      analyst: [],
      coach: [],
      manager: [],
      spectator: [],
      trial: [],
    };
    members.forEach((m) => {
      (base[m.roleLower] ||= []).push(m);
    });
    Object.keys(base).forEach((k) =>
      base[k].sort((a, b) => a.username.localeCompare(b.username))
    );
    return base;
  }, [members]);

  const coachingStaff = useMemo(() => {
    return [...byRole.analyst, ...byRole.coach].sort((a, b) =>
      a.username.localeCompare(b.username)
    );
  }, [byRole.analyst, byRole.coach]);

  const baseItems = useMemo(() => {
    let id = 0;
    const items = [];

    items.push({ id: id++, label: "@Players", kind: "group", group: "players", payload: {members: byRole.player} });

    byRole.player.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    if (coachingStaff.length) {
      items.push({
        id: id++,
        label: "@ Coaching Staff",
        kind: "group",
        group: "coaching",
        payload: { members: coachingStaff },
      });
    }

    if (byRole.sub.length) {
      items.push({
        id: id++,
        label: "@ Subs",
        kind: "group",
        group: "subs",
        payload: { members: byRole.sub },
      });
    }
    byRole.sub.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    if (byRole.manager.length) {
      items.push({
        id: id++,
        label: "@ Managers",
        kind: "group",
        group: "managers",
        payload: { members: byRole.manager },
      });
    }
    byRole.manager.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    byRole.spectator.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );
    byRole.trial.forEach((m) =>
      items.push({ id: id++, label: `@${m.username}`, kind: "user", payload: m })
    );

    return items;
  }, [byRole, coachingStaff]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [trigger, setTrigger] = useState({ start: -1, query: "" });

  const suggestions = useMemo(() => {
    const q = (trigger.query || "").trim().toLowerCase();
    if (!q) return baseItems;
    return baseItems.filter((it) =>
      it.label.replace(/^@ ?/, "").toLowerCase().startsWith(q)
    );
  }, [baseItems, trigger.query]);

  const updateMenuPosition = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    // anchor to wrapper so absolute positioning works as expected
    const parent = ta.parentElement;
    const taRect = ta.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    setMenuPos({
      top: taRect.bottom - parentRect.top + 6,
      left: taRect.left - parentRect.left + 12,
    });
  };

  const onTextChangeInline = (e) => {
    const val = e.target.value;
    setReplyText(val);

    const caret = e.target.selectionStart || 0;
    const uptoCaret = val.slice(0, caret);
    const match = uptoCaret.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);

    if (match) {
      const q = match[1];
      const atPos = caret - (q.length + 1);
      setTrigger({ start: atPos, query: q });
      setMenuOpen(true);
      setActiveIndex(0);
      updateMenuPosition();
    } else {
      setMenuOpen(false);
      setTrigger({ start: -1, query: "" });
    }
  };

  const applyPick = (item) => {
    const ta = textareaRef.current;
    if (!ta || trigger.start < 0) return;

    const before = replyText.slice(0, trigger.start);
    const after = replyText.slice(ta.selectionStart);

    let insertText = "";
    if (item.kind === "team") insertText = "@Team";
    else if (item.kind === "group") insertText = item.label;
    else insertText = `@${item.payload.username}`;

    const next = `${before}${insertText} ${after}`;
    setReplyText(next);

    const newPos = (before + insertText + " ").length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newPos, newPos);
    });

    setMenuOpen(false);
    setTrigger({ start: -1, query: "" });
  };

  const onTextKeyDownInline = (e) => {
    if (!menuOpen) return;

    if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) {
      e.preventDefault();
    }

    if (e.key === "ArrowDown") {
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setMenuOpen(false);
    } else if (e.key === "Enter" || e.key === "Tab") {
      const pick = suggestions[activeIndex] || suggestions[0];
      if (pick) applyPick(pick);
    }
  };

  // optional but recommended: store mentions for notifications / highlighting
  const extractMentions = (text) => addPlayerMentions(text, byRole.player, text.match(/@\w+/g) || []);

  // ─────────────────────────────────────────────
  // Path helper + guards (prevents matches/undefined)
  // ─────────────────────────────────────────────
  const getParentDocRef = () => {
    if (!teamId || !mapId) return null;

    // Preferred: match-based reviews
    if (matchId) {
      return doc(db, "teams", teamId, "maps", mapId, "matches", matchId);
    }

    // Legacy: video-based notes
    if (videoId) {
      return doc(db, "teams", teamId, "maps", mapId, "videos", videoId);
    }

    return null;
  };

  // ─────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────
  const handleSubmit = async () => {
    if (localSaving) return;
    if (!replyText.trim()) {
      setAlert({ message: "Reply cannot be empty.", type: "error", position: "top" });
      return;
    }

    if (!noteId) {
      console.error("[ReplyInputBox] Missing noteId:", { noteId, teamId, mapId, matchId, videoId });
      setAlert({ message: "Note is still loading. Please try again in a moment.", type: "error", position: "top" });
      return;
    }

    const mentions = extractMentions(replyText);

    if (localSubmit) {
      setLocalSaving(true);
      try {await localSubmit({text:replyText.trim(),mentions});setReplyText('');onReplyAdded?.();}
      catch(error) {setAlert({message:error.message||'Reply could not be saved. Your draft is preserved.',type:'error',position:'top'});}
      finally {setLocalSaving(false);}
      return;
    }
    const parentRef = getParentDocRef();
    if (!parentRef) {
      console.error("[ReplyInputBox] Missing ids:", { teamId, mapId, matchId, videoId, noteId });
      setAlert({ message: "Still loading match/video info. Please try again in a moment.", type: "error", position: "top" });
      return;
    }

    // replies live under either:
    // matches/{matchId}/allReplies  OR  videos/{videoId}/allReplies (legacy)
    const repliesColRef = collection(parentRef, "allReplies");

    await addDoc(repliesColRef, {
      noteId,
      text: replyText.trim(),
      at: mentions, // ✅ store mentions like notes do
      createdBy: username,
      createdByUid: auth.currentUser?.uid || "",
      createdAt: serverTimestamp(),
      viewers: [username],
    });

    // bump replyCount on the parent note doc
    const noteRef = doc(parentRef, "notes", noteId);

    await updateDoc(noteRef, {
      replyCount: increment(1),
      lastReplyAt: serverTimestamp(),
    });

    // If you want reply mentions to trigger your notifier, you can pass mentions here.
    // (Depends on how notifyTeamMembersExceptActor is implemented.)
    try {
      await notifyTeamMembersExceptActor?.({
        teamId,
        actorUsername: username,
        // include mapId/videoId/noteId if your notifier uses them
        mentions,
        type: "reply",
      });
    } catch {
      // ignore notifier failure, keep UX smooth
    }

    setReplyText("");
    onReplyAdded?.();
  };

  return (
    <div className="reply-input" style={{ position: "relative" }}>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          position={alert.position}
          onClose={() => setAlert(null)}
        />
      )}

      <textarea
        ref={textareaRef}
        className="ReplyInputBox-textarea"
        rows={2}
        disabled={localSaving}
        maxLength={350}
        value={replyText}
        onChange={onTextChangeInline}
        onKeyDown={onTextKeyDownInline}
        onFocus={updateMenuPosition}
        placeholder="Write a reply… type @ to mention"
      />

      <MentionMenu
        items={suggestions}
        open={menuOpen}
        style={menuPos}
        activeIndex={activeIndex}
        onHoverIndex={(i) => setActiveIndex(i)}
        onPick={applyPick}
      />

      <div className="reply-controls">
        <button type="button" className="ReplyInputBox-Send-btn" disabled={localSaving} onClick={handleSubmit}>
          Send
        </button>
        <button type="button" className="ReplyInputBox-cancel-btn app-cancel-control" disabled={localSaving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
